/**
 * Biome archetype templates — the layer above `presetBuilders`. Each archetype
 * bundles a complete biome layer stack (sky → celestial → clouds → motes → rays
 * → glow → lightField → weather → flicker → parallax → closer) with family-typical
 * defaults for every structural knob, so a NEW biome is ~10 lines of distinguishing
 * values (colours, sky gradient, parallax shapes, closer colours) instead of a
 * full 50-line layer list.
 *
 * The 8 archetypes cover the studio's biome spectrum:
 *   • `forestBiome()`    — bright/temperate sunlit biomes (meadow, coast, savanna, …)
 *   • `volcanicBiome()`  — hot/dark heat biomes (volcanic, crimson, ashlands, …)
 *   • `frozenBiome()`    — cold/icy biomes (frostspire, moor, tempest, …)
 *   • `aquaticBiome()`   — underwater/water biomes (underwater, coast-variant, …)
 *   • `aridBiome()`      — hot-dry sun-baked biomes (desert, savanna, ashlands, …)
 *   • `darkBiome()`      — ominous low-key dark biomes (corrupted, crimson, abyss, …)
 *   • `etherealBiome()`  — bright/dreamlike sky realms (celestial, radiant, skylands, …)
 *   • `stoneBiome()`     — enclosed low-light stone biomes (cave, dungeon, petrified, …)
 *   • `sunkenBiome()`    — vast interior vaults with drifting mega-silhouettes (sunken cathedral, …)
 *
 * Output-preserving: refactored presets pass every value that differs from the
 * family defaults, so the generated `EffectLayer[]` is identical to the original
 * inline literal (verified by `presets.snapshot.test.ts`). Optional layers
 * (celestial, cloudBands, fog, flicker) are omitted entirely when not passed —
 * no empty-placeholder fields leak into the output.
 */
import type { AtmospherePreset } from './presets.config';
import type { EffectLayer, ColorGradeSpec, VignetteSpec, CelestialSpec } from './effectLayer.types';
import type { GlowBlobSpec } from './presets.config';
import type { GlowTextureKey } from '../../game/rendering/glowTextures';
import type { ParallaxPlane } from './presetBuilders';
import type { SilhouetteShape } from '../../game/effects/atmosphere/silhouettes';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from './presetBuilders';
import { Z_LAYERS } from '../../game/rendering/Z_LAYERS';

// ─── merge helper ───────────────────────────────────────────────────────────
/** Merge defaults + config, stripping `undefined` values so absent fields don't
 *  leak into the output (the output-preserving guarantee: a field not in the
 *  defaults AND not in the config is absent from the emitted layer). */
function mergeSpec<D extends Record<string, unknown>>(defaults: D, config: Record<string, unknown> = {}): D {
  const out: Record<string, unknown> = { ...defaults, ...config };
  for (const k of Object.keys(out)) if (out[k] === undefined) delete out[k];
  return out as D;
}

// ─── shared config fragments ────────────────────────────────────────────────
interface CloudBandsSpec {
  count: number; color: number; sizeMin: number; sizeMax: number;
  alphaMin: number; alphaMax: number; driftPxPerSec: number; bandYMin: number; bandYMax: number;
}
interface MotesSpec {
  colors: number[];
  textureKey?: GlowTextureKey;
  count?: number; sizeMin?: number; sizeMax?: number; driftPx?: number;
  periodMinMs?: number; periodMaxMs?: number; alphaMin?: number; alphaMax?: number;
}
interface RaysSpec {
  color: number;
  count?: number; widthMin?: number; widthMax?: number; height?: number;
  tiltDeg?: number; periodMinMs?: number; periodMaxMs?: number; alphaMin?: number; alphaMax?: number;
}
interface GlowSpec {
  blobs: GlowBlobSpec[];
  breathe?: { periodMs: number; min: number; max: number; phase?: number };
}
interface LightFieldSpec {
  palette: number[];
  count?: number; sizeMin?: number; sizeMax?: number;
  alphaMin?: number; alphaMax?: number; driftRadius?: number;
  periodMinMs?: number; periodMaxMs?: number;
}
interface WeatherSpec {
  mode: 'rise' | 'fall';
  color: number;
  colorEnd?: number;
  size?: number; frequencyMs?: number; lifespanMs?: number;
  speed?: number; gravityY?: number; windX?: number; alphaEnd?: number;
  gust?: { periodMs: number; min: number; max: number; phase?: number };
}
interface FlickerSpec {
  color: number;
  size?: number; baseAlpha?: number; flickerAmp?: number;
  hzMin?: number; hzMax?: number;
  anchors?: { atX: number; atY: number }[];
}
interface FogSpec {
  color: number; bands: number;
  alphaMin: number; alphaMax: number; driftPx: number;
  periodMinMs: number; periodMaxMs: number;
  yMin?: number; yMax?: number; sizeMin?: number; sizeMax?: number;
}
interface CloserSpec {
  vignette?: Omit<VignetteSpec, 'kind'>;
  grade?: ColorGradeSpec;
}

/** Caustics spec (mirrors CausticsSpec from effectLayer.types — kept local for
 *  the archetype config surface, same as the other local spec interfaces). */
interface CausticsSpec {
  color: number; count: number;
  sizeMin: number; sizeMax: number;
  alphaMin: number; alphaMax: number;
  bandYMin: number; bandYMax: number;
  driftPx: number; periodMinMs: number; periodMaxMs: number;
}

/** Leviathan plane (one depth band of drifting mega-silhouettes). */
interface LeviathanPlane {
  count: number;
  widthMin: number; widthMax: number;
  speedMin: number; speedMax: number;
  alpha: number; tint: number; depth: number;
}

/** Leviathans spec (mirrors LeviathanSpec from effectLayer.types). */
interface LeviathanSpec {
  shape: SilhouetteShape;
  planes: LeviathanPlane[];
  bob: { ampPx: number; periodMinMs: number; periodMaxMs: number };
  edgeHighlight?: boolean;
  edgeColor?: number;
  detail?: number;
  direction?: 'left' | 'right' | 'both';
  bandYMin?: number; bandYMax?: number;
}

/** Schooling motes spec (mirrors SchoolingMotesSpec from effectLayer.types). */
interface SchoolingMotesSpec {
  count: number; schools?: number;
  palette: number[];
  sizeMin: number; sizeMax: number;
  speed: number;
  cohesion?: number; alignment?: number; separation?: number;
  bandYMin?: number; bandYMax?: number;
  textureKey?: GlowTextureKey;
  startle?: boolean;
}

// ─── standard 3-anchor floor pattern (forest/frozen/volcanic) ───────────────
const FLOOR_ANCHORS = [{ atX: 0.25, atY: 0.92 }, { atX: 0.55, atY: 0.95 }, { atX: 0.82, atY: 0.92 }];
const VOLCANIC_ANCHORS = [{ atX: 0.2, atY: 0.9 }, { atX: 0.5, atY: 0.95 }, { atX: 0.8, atY: 0.9 }];

// ─── archetype configs ──────────────────────────────────────────────────────
export interface ForestBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  celestial?: CelestialSpec;
  cloudBands?: CloudBandsSpec;
  motes: MotesSpec;
  rays: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  weather: WeatherSpec;
  flicker?: FlickerSpec;
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
  /** Extra layers inserted before the parallax block (caustics, critters, …). */
  extraLayers?: EffectLayer[];
}

export interface VolcanicBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  celestial?: CelestialSpec;
  motes: MotesSpec;
  rays: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  weather: WeatherSpec;
  flicker?: FlickerSpec;
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
  /** Extra layers inserted after godRays, before glow (lightning, …). */
  afterRaysLayers?: EffectLayer[];
  /** Extra layers inserted before the parallax block. */
  extraLayers?: EffectLayer[];
}

export interface FrozenBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  celestial?: CelestialSpec;
  cloudBands?: CloudBandsSpec;
  motes: MotesSpec;
  rays?: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  weather?: WeatherSpec;
  flicker?: FlickerSpec;
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
  extraLayers?: EffectLayer[];
}

export interface AquaticBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  motes: MotesSpec;
  rays: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  weather: WeatherSpec;
  flicker?: FlickerSpec;
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
  /** Extra layers inserted before fog (caustics, …). */
  extraLayers?: EffectLayer[];
  /** Extra layers inserted after fog, before parallax (critters, …). */
  postFogLayers?: EffectLayer[];
}

export interface AridBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  celestial?: CelestialSpec;
  cloudBands?: CloudBandsSpec;
  motes: MotesSpec;
  rays: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  weather: WeatherSpec;
  flicker?: FlickerSpec;
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
  /** Extra layers inserted before the parallax block (heatHaze, …). */
  extraLayers?: EffectLayer[];
  /** Swap motes/rays order: rays before motes (desert). Default: motes → rays. */
  raysBeforeMotes?: boolean;
}

export interface DarkBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  celestial?: CelestialSpec;
  motes: MotesSpec;
  rays: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  weather: WeatherSpec;
  flicker?: FlickerSpec;
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
  /** Extra layers inserted after rays, before glow (lightning, …). */
  afterRaysLayers?: EffectLayer[];
  /** Extra layers inserted after lightField, before weather (vortex, bioluminescence, …). */
  extraAtmosphereLayers?: EffectLayer[];
}

export interface EtherealBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  celestial?: CelestialSpec;
  cloudBands?: CloudBandsSpec;
  /** Extra sky layers inserted after cloudBands, before motes (aurora, shootingStar, …). */
  extraSkyLayers?: EffectLayer[];
  motes: MotesSpec;
  rays: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  weather: WeatherSpec;
  flicker?: FlickerSpec;
  /** Extra layers inserted before the parallax block. */
  extraLayers?: EffectLayer[];
  /** 2 or 3 parallax planes (celestial uses 2; radiant/skylands use 3). */
  parallax: ParallaxPlane[];
  fog?: FogSpec;
  closer: CloserSpec;
}

export interface StoneBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  celestial?: CelestialSpec;
  motes: MotesSpec;
  /** Optional — cave has no rays. */
  rays?: RaysSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  /** Optional — cave/dungeon have no weather. */
  weather?: WeatherSpec;
  flicker?: FlickerSpec;
  /** Extra layers inserted before the parallax block (dungeon fog, …). */
  extraLayers?: EffectLayer[];
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
}

/**
 * Sunken biome archetype — vast interior vaults with drifting mega-silhouettes
 * (the sunken cathedral). The signature stack: a near-black sky → sparse motes
 * → a dim glow bed → a sparse light field → optional caustics → LEVIATHANS
 * (the whale-skeleton drift, 2 depth planes) → optional SCHOOLING MOTES (life
 * between the bones) → a 3-plane vault parallax → heavy fog → a heavy vignette
 * + dark colour-grade closer. Interior opt-out (no celestial, no day-clock).
 *
 * Stack: sky → motes → glow → lightField → caustics? → leviathans →
 *        schoolingMotes? → extraLayers? → parallax → fog → closer
 *
 * A new sunken biome needs only: `id`, `sky`, `motes.colors`, `glow`,
 * `lightField.palette`, `leviathans` (planes + tints), `parallax`, `closer` —
 * ~10 lines (caustics/schooling/fog all optional with sensible defaults).
 *
 * @see docs/design/systems/sunken-biome-archetype.md — blueprint.
 */
export interface SunkenBiomeConfig {
  id: string;
  sky: { from: number; to: number; bands?: number };
  motes: MotesSpec;
  glow: GlowSpec;
  lightField: LightFieldSpec;
  /** Optional caustic shimmer on the vault floor. */
  caustics?: CausticsSpec;
  /** The leviathans layer (1–3 drifting mega-silhouettes across 1–2 planes). */
  leviathans: LeviathanSpec;
  /** Optional schooling motes (life between the bones). */
  schoolingMotes?: SchoolingMotesSpec;
  /** Extra layers inserted before the parallax block. */
  extraLayers?: EffectLayer[];
  parallax: Parameters<typeof multiPlaneParallax>[0];
  fog?: FogSpec;
  closer: CloserSpec;
}

// ─── family-typical defaults ────────────────────────────────────────────────
const FOREST_MOTE_DEFAULTS = { count: 22, sizeMin: 4, sizeMax: 11, driftPx: 42, periodMinMs: 5500, periodMaxMs: 12000, alphaMin: 0.16, alphaMax: 0.55 };
const FOREST_RAY_DEFAULTS = { count: 4, widthMin: 70, widthMax: 150, height: 540, tiltDeg: 8, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.08, alphaMax: 0.28 };
const FOREST_LIGHTFIELD_DEFAULTS = { count: 12, sizeMin: 120, sizeMax: 300, alphaMin: 0.04, alphaMax: 0.13, driftRadius: 60, periodMinMs: 7000, periodMaxMs: 13000 };
const FOREST_WEATHER_DEFAULTS = { size: 6, frequencyMs: 110, lifespanMs: 5200, speed: 14, gravityY: 4, windX: 48, alphaEnd: 0.5 };
const FOREST_FLICKER_DEFAULTS = { size: 280, baseAlpha: 0.05, flickerAmp: 0.04, hzMin: 2, hzMax: 5, anchors: FLOOR_ANCHORS };

const VOLCANIC_MOTE_DEFAULTS = { count: 22, sizeMin: 6, sizeMax: 16, driftPx: 36, periodMinMs: 5000, periodMaxMs: 11000, alphaMin: 0.25, alphaMax: 0.85 };
const VOLCANIC_RAY_DEFAULTS = { count: 3, widthMin: 60, widthMax: 140, height: 500, tiltDeg: 8, periodMinMs: 7000, periodMaxMs: 13000, alphaMin: 0.12, alphaMax: 0.4 };
const VOLCANIC_LIGHTFIELD_DEFAULTS = { count: 14, sizeMin: 110, sizeMax: 280, alphaMin: 0.05, alphaMax: 0.18, driftRadius: 70, periodMinMs: 6000, periodMaxMs: 12000 };
const VOLCANIC_FLICKER_DEFAULTS = { size: 280, baseAlpha: 0.08, flickerAmp: 0.06, hzMin: 3, hzMax: 7, anchors: VOLCANIC_ANCHORS };

const FROZEN_MOTE_DEFAULTS = { count: 22, sizeMin: 4, sizeMax: 11, driftPx: 40, periodMinMs: 6000, periodMaxMs: 13000, alphaMin: 0.15, alphaMax: 0.55 };
const FROZEN_RAY_DEFAULTS = { count: 4, widthMin: 60, widthMax: 150, height: 540, tiltDeg: 9, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.07, alphaMax: 0.26 };
const FROZEN_LIGHTFIELD_DEFAULTS = { count: 12, sizeMin: 110, sizeMax: 280, alphaMin: 0.04, alphaMax: 0.13, driftRadius: 60, periodMinMs: 7000, periodMaxMs: 13000 };
const FROZEN_WEATHER_DEFAULTS = { size: 6, frequencyMs: 100, lifespanMs: 5200, speed: 16, gravityY: 8, windX: 40, alphaEnd: 0.5 };
const FROZEN_FLICKER_DEFAULTS = { size: 280, baseAlpha: 0.05, flickerAmp: 0.04, hzMin: 2, hzMax: 5, anchors: FLOOR_ANCHORS };

const AQUATIC_MOTE_DEFAULTS = { count: 20, sizeMin: 8, sizeMax: 18, driftPx: 22, periodMinMs: 10000, periodMaxMs: 19000, alphaMin: 0.18, alphaMax: 0.6 };
const AQUATIC_RAY_DEFAULTS = { count: 4, widthMin: 70, widthMax: 150, height: 540, tiltDeg: 12, periodMinMs: 10000, periodMaxMs: 18000, alphaMin: 0.12, alphaMax: 0.4 };
const AQUATIC_LIGHTFIELD_DEFAULTS = { count: 14, sizeMin: 100, sizeMax: 240, alphaMin: 0.05, alphaMax: 0.17, driftRadius: 60, periodMinMs: 10000, periodMaxMs: 18000 };
const AQUATIC_FLICKER_DEFAULTS = { size: 320, baseAlpha: 0.06, flickerAmp: 0.05, hzMin: 0.5, hzMax: 1.5, anchors: [{ atX: 0.3, atY: 0.4 }, { atX: 0.7, atY: 0.55 }, { atX: 0.5, atY: 0.25 }] };

const ARID_MOTE_DEFAULTS = { count: 24, sizeMin: 4, sizeMax: 12, driftPx: 44, periodMinMs: 6000, periodMaxMs: 13000, alphaMin: 0.14, alphaMax: 0.55 };
const ARID_RAY_DEFAULTS = { count: 4, widthMin: 70, widthMax: 160, height: 540, tiltDeg: 10, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.10, alphaMax: 0.34 };
const ARID_LIGHTFIELD_DEFAULTS = { count: 12, sizeMin: 120, sizeMax: 300, alphaMin: 0.04, alphaMax: 0.14, driftRadius: 60, periodMinMs: 7000, periodMaxMs: 13000 };
const ARID_WEATHER_DEFAULTS = { size: 7, frequencyMs: 90, lifespanMs: 4200, speed: 18, gravityY: 6, windX: 64, alphaEnd: 0.5 };
const ARID_FLICKER_DEFAULTS = { size: 300, baseAlpha: 0.06, flickerAmp: 0.05, hzMin: 2, hzMax: 5, anchors: FLOOR_ANCHORS };

const DARK_MOTE_DEFAULTS = { count: 24, sizeMin: 4, sizeMax: 13, driftPx: 38, periodMinMs: 5000, periodMaxMs: 11000, alphaMin: 0.2, alphaMax: 0.8 };
const DARK_RAY_DEFAULTS = { count: 3, widthMin: 50, widthMax: 130, height: 520, tiltDeg: 6, periodMinMs: 6000, periodMaxMs: 12000, alphaMin: 0.10, alphaMax: 0.36 };
const DARK_LIGHTFIELD_DEFAULTS = { count: 14, sizeMin: 110, sizeMax: 280, alphaMin: 0.05, alphaMax: 0.2, driftRadius: 75, periodMinMs: 5500, periodMaxMs: 11000 };
const DARK_WEATHER_DEFAULTS = { size: 11, frequencyMs: 120, lifespanMs: 3800, speed: 30, gravityY: -16, windX: 18, alphaEnd: 0.55 };
const DARK_FLICKER_DEFAULTS = { size: 280, baseAlpha: 0.08, flickerAmp: 0.07, hzMin: 3, hzMax: 8, anchors: VOLCANIC_ANCHORS };

const ETHEREAL_MOTE_DEFAULTS = { count: 22, sizeMin: 4, sizeMax: 12, driftPx: 30, periodMinMs: 6000, periodMaxMs: 13000, alphaMin: 0.2, alphaMax: 0.7 };
const ETHEREAL_RAY_DEFAULTS = { count: 4, widthMin: 70, widthMax: 160, height: 560, tiltDeg: 6, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.08, alphaMax: 0.3 };
const ETHEREAL_LIGHTFIELD_DEFAULTS = { count: 14, sizeMin: 120, sizeMax: 300, alphaMin: 0.04, alphaMax: 0.15, driftRadius: 60, periodMinMs: 7000, periodMaxMs: 13000 };
const ETHEREAL_WEATHER_DEFAULTS = { size: 8, frequencyMs: 120, lifespanMs: 4600, speed: 16, gravityY: -10, windX: 12, alphaEnd: 0.5 };
const ETHEREAL_FLICKER_DEFAULTS = { size: 260, baseAlpha: 0.05, flickerAmp: 0.04, hzMin: 2, hzMax: 5, anchors: FLOOR_ANCHORS };

const STONE_MOTE_DEFAULTS = { count: 14, sizeMin: 6, sizeMax: 14, driftPx: 20, periodMinMs: 8000, periodMaxMs: 16000, alphaMin: 0.15, alphaMax: 0.5 };
const STONE_RAY_DEFAULTS = { count: 3, widthMin: 50, widthMax: 120, height: 480, tiltDeg: 8, periodMinMs: 9000, periodMaxMs: 16000, alphaMin: 0.08, alphaMax: 0.28 };
const STONE_LIGHTFIELD_DEFAULTS = { count: 11, sizeMin: 80, sizeMax: 200, alphaMin: 0.04, alphaMax: 0.15, driftRadius: 40, periodMinMs: 9000, periodMaxMs: 16000 };
const STONE_WEATHER_DEFAULTS = { size: 7, frequencyMs: 130, lifespanMs: 5200, speed: 12, gravityY: 5, windX: 26, alphaEnd: 0.45 };
const STONE_FLICKER_DEFAULTS = { size: 200, baseAlpha: 0.1, flickerAmp: 0.07, hzMin: 1, hzMax: 4, anchors: FLOOR_ANCHORS };

// ─── sunken biome palette + defaults (BUILD_STANDARDS §1 — named constants) ──
const SUNKEN_PALETTE = {
  skyFrom:     0x05100e,  // near-black green (vault ceiling wash start)
  skyTo:       0x0c2a26,  // deep teal-green (vault ceiling wash end)
  boneFar:     0x8fbfb8,  // dim cold bone (far plane tint — LIT/additive)
  boneNear:    0xbfeae0,  // bright cold bone (near plane tint — LIT/additive)
  gradeTop:    0x05100e,  // near-black green (colorGrade wash top)
  gradeBottom: 0x0c2a26,  // deep teal (colorGrade wash bottom)
  vignette:    0x000000,  // heavy black edge vignette
} as const;

const SUNKEN_MOTE_DEFAULTS = { count: 12, sizeMin: 5, sizeMax: 12, driftPx: 18, periodMinMs: 12000, periodMaxMs: 24000, alphaMin: 0.12, alphaMax: 0.45 };
const SUNKEN_LIGHTFIELD_DEFAULTS = { count: 10, sizeMin: 100, sizeMax: 220, alphaMin: 0.03, alphaMax: 0.12, driftRadius: 50, periodMinMs: 10000, periodMaxMs: 20000 };
const SUNKEN_CAUSTIC_DEFAULTS = { color: 0x4a9e98, count: 5, sizeMin: 120, sizeMax: 280, alphaMin: 0.04, alphaMax: 0.12, bandYMin: 0.7, bandYMax: 0.95, driftPx: 30, periodMinMs: 8000, periodMaxMs: 16000 };
const SUNKEN_LEVIATHAN_DEFAULTS = {
  shape: 'whaleSkeleton' as const,
  planes: [
    { count: 1, widthMin: 200, widthMax: 400, speedMin: 8, speedMax: 14, alpha: 0.12, tint: SUNKEN_PALETTE.boneFar, depth: Z_LAYERS.LEVIATHAN_FAR },
    { count: 1, widthMin: 500, widthMax: 800, speedMin: 5, speedMax: 10, alpha: 0.18, tint: SUNKEN_PALETTE.boneNear, depth: Z_LAYERS.LEVIATHAN_NEAR },
  ],
  bob: { ampPx: 12, periodMinMs: 8000, periodMaxMs: 16000 },
  edgeHighlight: true,
  edgeColor: SUNKEN_PALETTE.boneNear,
  detail: 1,
  direction: 'both' as const,
  bandYMin: 0.1,
  bandYMax: 0.45,
};
const SUNKEN_SCHOOLING_DEFAULTS = { count: 24, schools: 2, sizeMin: 3, sizeMax: 7, speed: 22, cohesion: 0.3, alignment: 0.4, separation: 0.5, bandYMin: 0.3, bandYMax: 0.7, startle: true };

// ─── layer builders (internal) ──────────────────────────────────────────────
function motesLayer(spec: MotesSpec, defaults: Record<string, unknown>): EffectLayer {
  const m = mergeSpec(defaults, spec as unknown as Record<string, unknown>);
  return { kind: 'fireflies', ...m } as EffectLayer;
}
function raysLayer(spec: RaysSpec, defaults: Record<string, unknown>): EffectLayer {
  const m = mergeSpec(defaults, spec as unknown as Record<string, unknown>);
  return { kind: 'godRays', ...m } as EffectLayer;
}
function lightFieldLayer(spec: LightFieldSpec, defaults: Record<string, unknown>): EffectLayer {
  const m = mergeSpec(defaults, spec as unknown as Record<string, unknown>);
  return { kind: 'lightField', ...m } as EffectLayer;
}
function glowLayer(spec: GlowSpec): EffectLayer {
  if (spec.breathe) return breathingGlowBlobs(spec.blobs, spec.breathe);
  return { kind: 'glowBlobs', blobs: spec.blobs };
}
function weatherLayer(spec: WeatherSpec, defaults: Record<string, unknown>): EffectLayer {
  const { gust, ...rest } = spec;
  const m = mergeSpec(defaults, rest as unknown as Record<string, unknown>);
  if (gust) return gustingWeather(m, gust);
  return { kind: 'weather', ...m } as EffectLayer;
}
function flickerLayer(spec: FlickerSpec, defaults: Record<string, unknown>): EffectLayer {
  const m = mergeSpec(defaults, spec as unknown as Record<string, unknown>);
  return { kind: 'flicker', ...m } as EffectLayer;
}
function fogLayer(spec: FogSpec): EffectLayer {
  return { kind: 'fog', ...spec } as EffectLayer;
}

// ─── archetypes ─────────────────────────────────────────────────────────────

/**
 * Forest biome archetype — bright/temperate sunlit biomes with a warm sun,
 * drifting clouds, motes, sun shafts, a breathing glow bed, a light field,
 * falling weather (leaves/petals/pollen), a floor flicker, a 3-plane parallax
 * backdrop, and a vignette + colour-grade closer.
 *
 * Stack: sky → celestial? → cloudBands? → motes → rays → glow → lightField →
 *        weather → flicker? → extraLayers? → parallax → fog? → closer
 *
 * A new forest biome needs only: `id`, `sky`, `motes.colors`, `rays.color`,
 * `glow`, `lightField.palette`, `weather` (colours + mode + gust), `parallax`,
 * `closer` — ~10 lines. Structural knobs default to family-typical values.
 */
export function forestBiome(cfg: ForestBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  if (cfg.celestial) layers.push({ kind: 'celestial', ...cfg.celestial });
  if (cfg.cloudBands) layers.push({ kind: 'cloudBands', ...cfg.cloudBands });
  layers.push(motesLayer(cfg.motes, FOREST_MOTE_DEFAULTS));
  layers.push(raysLayer(cfg.rays, FOREST_RAY_DEFAULTS));
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, FOREST_LIGHTFIELD_DEFAULTS));
  layers.push(weatherLayer(cfg.weather, FOREST_WEATHER_DEFAULTS));
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, FOREST_FLICKER_DEFAULTS));
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  layers.push(...multiPlaneParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/**
 * Volcanic biome archetype — hot/dark heat biomes with ember motes, heat
 * shafts, a breathing lava/ember glow bed, a restless light field, rising
 * embers (or falling ash), a heat flicker, a 3-plane dark silhouette parallax,
 * optional fog, and a vignette + colour-grade closer.
 *
 * Stack: sky → celestial? → motes → rays → afterRaysLayers? → glow → lightField
 *        → weather → flicker? → extraLayers? → parallax → fog? → closer
 *
 * A new volcanic biome needs only: `id`, `sky`, `motes.colors`, `rays.color`,
 * `glow`, `lightField.palette`, `weather` (colours + mode + gust), `parallax`,
 * `closer` — ~10 lines.
 */
export function volcanicBiome(cfg: VolcanicBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  if (cfg.celestial) layers.push({ kind: 'celestial', ...cfg.celestial });
  layers.push(motesLayer(cfg.motes, VOLCANIC_MOTE_DEFAULTS));
  layers.push(raysLayer(cfg.rays, VOLCANIC_RAY_DEFAULTS));
  if (cfg.afterRaysLayers) layers.push(...cfg.afterRaysLayers);
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, VOLCANIC_LIGHTFIELD_DEFAULTS));
  layers.push(weatherLayer(cfg.weather, {}));  // volcanic weather has no family-typical defaults (varies: rise/fall, gravity, wind)
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, VOLCANIC_FLICKER_DEFAULTS));
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  layers.push(...multiPlaneParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/**
 * Frozen biome archetype — cold/icy biomes with a pale moon/sun, high cloud
 * bands, ice/snow motes, cold sun shafts, a breathing frost glow bed, a crisp
 * light field, wind-blown snow, a cold floor glint, a 3-plane ice peak parallax,
 * optional fog, and a closer.
 *
 * Stack: sky → celestial? → cloudBands? → motes → rays? → glow → lightField →
 *        weather? → flicker? → extraLayers? → parallax → fog? → closer
 *
 * A new frozen biome needs only: `id`, `sky`, `motes.colors`, `glow`,
 * `lightField.palette`, `parallax`, `closer` — ~8 lines (rays/weather/flicker
 * optional).
 */
export function frozenBiome(cfg: FrozenBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  if (cfg.celestial) layers.push({ kind: 'celestial', ...cfg.celestial });
  if (cfg.cloudBands) layers.push({ kind: 'cloudBands', ...cfg.cloudBands });
  layers.push(motesLayer(cfg.motes, FROZEN_MOTE_DEFAULTS));
  if (cfg.rays) layers.push(raysLayer(cfg.rays, FROZEN_RAY_DEFAULTS));
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, FROZEN_LIGHTFIELD_DEFAULTS));
  if (cfg.weather) layers.push(weatherLayer(cfg.weather, FROZEN_WEATHER_DEFAULTS));
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, FROZEN_FLICKER_DEFAULTS));
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  layers.push(...multiPlaneParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/**
 * Aquatic biome archetype — underwater/water biomes with biolume motes, cyan
 * shafts, a breathing seabed glow, a drifting light field, rising bubbles,
 * a caustic flicker, optional caustics/critters, a 3-plane seabed parallax,
 * fog, and a colour-grade closer.
 *
 * Stack: sky → motes → rays → glow → lightField → weather → flicker? →
 *        extraLayers? → parallax → fog? → closer
 *
 * A new aquatic biome needs only: `id`, `sky`, `motes.colors`, `rays.color`,
 * `glow`, `lightField.palette`, `weather` (colours + mode + gust), `parallax`,
 * `closer` — ~10 lines.
 */
export function aquaticBiome(cfg: AquaticBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  layers.push(motesLayer(cfg.motes, AQUATIC_MOTE_DEFAULTS));
  layers.push(raysLayer(cfg.rays, AQUATIC_RAY_DEFAULTS));
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, AQUATIC_LIGHTFIELD_DEFAULTS));
  layers.push(weatherLayer(cfg.weather, {}));  // aquatic weather varies (no colorEnd/gravityY in some)
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, AQUATIC_FLICKER_DEFAULTS));
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  if (cfg.postFogLayers) layers.push(...cfg.postFogLayers);
  layers.push(...multiPlaneParallax(cfg.parallax));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/**
 * Arid biome archetype — hot-dry sun-baked biomes with a bright sun, optional
 * cloud bands, dust/pollen motes, hot sun shafts, a breathing sun-bloom glow,
 * a shimmering heat light field, blowing sand/dust on the wind, a faint
 * ground-level heat flicker, a 3-plane parallax backdrop, optional fog, and a
 * vignette + colour-grade closer.
 *
 * Stack: sky → celestial? → cloudBands? → [rays|motes] → glow → lightField →
 *        weather → flicker? → extraLayers? → parallax → fog? → closer
 *
 * `raysBeforeMotes` swaps the motes/rays order (desert puts shafts before dust;
 * savanna/ashlands put dust before shafts). Default: motes → rays.
 *
 * A new arid biome needs only: `id`, `sky`, `motes.colors`, `rays.color`,
 * `glow`, `lightField.palette`, `weather` (colours + mode + gust), `parallax`,
 * `closer` — ~10 lines.
 */
export function aridBiome(cfg: AridBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  if (cfg.celestial) layers.push({ kind: 'celestial', ...cfg.celestial });
  if (cfg.cloudBands) layers.push({ kind: 'cloudBands', ...cfg.cloudBands });
  if (cfg.raysBeforeMotes) {
    layers.push(raysLayer(cfg.rays, ARID_RAY_DEFAULTS));
    layers.push(motesLayer(cfg.motes, ARID_MOTE_DEFAULTS));
  } else {
    layers.push(motesLayer(cfg.motes, ARID_MOTE_DEFAULTS));
    layers.push(raysLayer(cfg.rays, ARID_RAY_DEFAULTS));
  }
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, ARID_LIGHTFIELD_DEFAULTS));
  layers.push(weatherLayer(cfg.weather, ARID_WEATHER_DEFAULTS));
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, ARID_FLICKER_DEFAULTS));
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  layers.push(...multiPlaneParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/**
 * Dark biome archetype — ominous low-key dark biomes with optional celestial,
 * sparse eerie motes, dim cold/sickly light shafts, optional after-rays layers
 * (lightning, …), a breathing low-key glow bed, a sparse dim light field,
 * optional extra atmosphere layers (vortex swirl, bioluminescence, …), rising
 * dark weather (spores/embers/cold motes), a pulsing floor flicker, a 3-plane
 * dark silhouette parallax, optional fog, and a heavy vignette + dark grade
 * closer.
 *
 * Stack: sky → celestial? → motes → rays → afterRaysLayers? → glow → lightField
 *        → extraAtmosphereLayers? → weather → flicker? → parallax → fog? → closer
 *
 * A new dark biome needs only: `id`, `sky`, `motes.colors`, `rays.color`,
 * `glow`, `lightField.palette`, `weather` (colours + mode + gust), `parallax`,
 * `closer` — ~10 lines.
 */
export function darkBiome(cfg: DarkBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  if (cfg.celestial) layers.push({ kind: 'celestial', ...cfg.celestial });
  layers.push(motesLayer(cfg.motes, DARK_MOTE_DEFAULTS));
  layers.push(raysLayer(cfg.rays, DARK_RAY_DEFAULTS));
  if (cfg.afterRaysLayers) layers.push(...cfg.afterRaysLayers);
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, DARK_LIGHTFIELD_DEFAULTS));
  if (cfg.extraAtmosphereLayers) layers.push(...cfg.extraAtmosphereLayers);
  layers.push(weatherLayer(cfg.weather, DARK_WEATHER_DEFAULTS));
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, DARK_FLICKER_DEFAULTS));
  layers.push(...multiPlaneParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/** Variable-length parallax (2 or 3 planes) — assigns FAR/MID[/NEAR] depths. */
function variableParallax(planes: ParallaxPlane[]): EffectLayer[] {
  const depths = [Z_LAYERS.PARALLAX_FAR, Z_LAYERS.PARALLAX_MID, Z_LAYERS.PARALLAX_NEAR] as const;
  return planes.map((plane, i) => ({ kind: 'parallax', ...plane, y: 0, depth: depths[i] }));
}

/**
 * Ethereal biome archetype — bright/dreamlike sky realms with a luminous
 * celestial body (sun/moon), drifting cloud bands, optional extra sky layers
 * (aurora, shooting stars, …), soft motes, gentle light shafts, a breathing
 * glow bed, a dreamy light field, rising weather (stardust/ascending motes),
 * a faint shimmer, a 2-or-3-plane parallax backdrop, optional haze, and a
 * vignette + colour-grade closer.
 *
 * Stack: sky → celestial? → cloudBands? → extraSkyLayers? → motes → rays →
 *        glow → lightField → weather → flicker? → extraLayers? → parallax →
 *        fog? → closer
 *
 * `parallax` accepts 2 or 3 planes (celestial uses 2; radiant/skylands use 3).
 *
 * A new ethereal biome needs only: `id`, `sky`, `motes.colors`, `rays.color`,
 * `glow`, `lightField.palette`, `weather` (colours + mode + gust), `parallax`,
 * `closer` — ~10 lines.
 */
export function etherealBiome(cfg: EtherealBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  if (cfg.celestial) layers.push({ kind: 'celestial', ...cfg.celestial });
  if (cfg.cloudBands) layers.push({ kind: 'cloudBands', ...cfg.cloudBands });
  if (cfg.extraSkyLayers) layers.push(...cfg.extraSkyLayers);
  layers.push(motesLayer(cfg.motes, ETHEREAL_MOTE_DEFAULTS));
  layers.push(raysLayer(cfg.rays, ETHEREAL_RAY_DEFAULTS));
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, ETHEREAL_LIGHTFIELD_DEFAULTS));
  layers.push(weatherLayer(cfg.weather, ETHEREAL_WEATHER_DEFAULTS));
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, ETHEREAL_FLICKER_DEFAULTS));
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  layers.push(...variableParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/**
 * Stone biome archetype — enclosed low-light stone biomes with optional
 * celestial, sparse motes, optional dim light shafts, a breathing glow bed,
 * a sparse light field, optional settling weather (dust/ash), a flickering
 * shimmer, a 3-plane dark silhouette parallax, optional fog, and a vignette +
 * colour-grade closer.
 *
 * Stack: sky → celestial? → motes → rays? → glow → lightField → weather? →
 *        flicker? → extraLayers? → parallax → fog? → closer
 *
 * A new stone biome needs only: `id`, `sky`, `motes.colors`, `glow`,
 * `lightField.palette`, `parallax`, `closer` — ~7 lines (rays/weather/flicker
 * all optional).
 */
export function stoneBiome(cfg: StoneBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  if (cfg.celestial) layers.push({ kind: 'celestial', ...cfg.celestial });
  layers.push(motesLayer(cfg.motes, STONE_MOTE_DEFAULTS));
  if (cfg.rays) layers.push(raysLayer(cfg.rays, STONE_RAY_DEFAULTS));
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, STONE_LIGHTFIELD_DEFAULTS));
  if (cfg.weather) layers.push(weatherLayer(cfg.weather, STONE_WEATHER_DEFAULTS));
  if (cfg.flicker) layers.push(flickerLayer(cfg.flicker, STONE_FLICKER_DEFAULTS));
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  layers.push(...multiPlaneParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}

/**
 * Sunken biome archetype — vast interior vaults with drifting mega-silhouettes
 * (the sunken cathedral). The signature stack: a near-black sky → sparse motes
 * → a dim glow bed → a sparse light field → optional caustics → LEVIATHANS
 * (the whale-skeleton drift, 2 depth planes) → optional SCHOOLING MOTES (life
 * between the bones) → a 3-plane vault parallax → heavy fog → a heavy vignette
 * + dark colour-grade closer. Interior opt-out (no celestial, no day-clock).
 *
 * Stack: sky → motes → glow → lightField → caustics? → leviathans →
 *        schoolingMotes? → extraLayers? → parallax → fog? → closer
 *
 * A new sunken biome needs only: `id`, `sky`, `motes.colors`, `glow`,
 * `lightField.palette`, `leviathans` (planes + tints), `parallax`, `closer` —
 * ~10 lines (caustics/schooling/fog all optional with sensible defaults).
 *
 * @see docs/design/systems/sunken-biome-archetype.md — blueprint.
 */
export function sunkenBiome(cfg: SunkenBiomeConfig): AtmospherePreset {
  const layers: EffectLayer[] = [
    { kind: 'skyGradient', ...cfg.sky },
  ];
  layers.push(motesLayer(cfg.motes, SUNKEN_MOTE_DEFAULTS));
  layers.push(glowLayer(cfg.glow));
  layers.push(lightFieldLayer(cfg.lightField, SUNKEN_LIGHTFIELD_DEFAULTS));
  if (cfg.caustics) {
    layers.push({ kind: 'caustics', ...mergeSpec(SUNKEN_CAUSTIC_DEFAULTS, cfg.caustics as unknown as Record<string, unknown>) });
  }
  // Leviathans — the signature layer. Merge with defaults so a consumer can
  // pass just the planes/tints and inherit the bob/edge/band defaults.
  layers.push({ kind: 'leviathans', ...mergeSpec(SUNKEN_LEVIATHAN_DEFAULTS, cfg.leviathans as unknown as Record<string, unknown>) } as EffectLayer);
  if (cfg.schoolingMotes) {
    layers.push({ kind: 'schoolingMotes', ...mergeSpec(SUNKEN_SCHOOLING_DEFAULTS, cfg.schoolingMotes as unknown as Record<string, unknown>) } as EffectLayer);
  }
  if (cfg.extraLayers) layers.push(...cfg.extraLayers);
  layers.push(...multiPlaneParallax(cfg.parallax));
  if (cfg.fog) layers.push(fogLayer(cfg.fog));
  layers.push(...biomeCloser(cfg.closer));
  return { id: cfg.id, layers };
}
